// client/src/components/notes/NotesList.jsx
//
// Dashboard → Notes. Modeled on EventList: search + date filter bar, filter
// tabs, desktop action bar + mobile footer. Notes can be narrowed to any open
// event, event item, event action, post or campaign they're associated with.
// Unassociated notes always stay at the top of the list.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { FaHome, FaPlus } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import SearchFilterBar from '../shared/SearchFilterBar';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import EmptyState from '../common/EmptyState';
import { listNotes, listNoteTargets } from '../../services/notesApi';
import NoteEditorModal from './NoteEditorModal';
import NoteCard from './NoteCard';
import { NOTE_REF_TYPES, typePlural, linkKey, flattenTargets } from './noteTypes';
import IconButton from '../common/IconButton';
import './Notes.css';

const toDate = (v) => {
  if (!v) return null;
  const d = new Date(typeof v === 'string' && v.includes(' ') && !v.includes('T') ? v.replace(' ', 'T') : v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Unassociated first, then most recently modified (mirrors the server). */
export function sortNotes(notes) {
  return [...notes].sort((a, b) => {
    const ua = (a.links || []).length === 0 ? 0 : 1;
    const ub = (b.links || []).length === 0 ? 0 : 1;
    if (ua !== ub) return ua - ub;
    return ((toDate(b.modifiedon)?.getTime() || 0) - (toDate(a.modifiedon)?.getTime() || 0)) || b.id - a.id;
  });
}

export default function NotesList({ tenantId: tenantProp }) {
  const params = useParams();
  const tenant = tenantProp || params.tenant;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [notes, setNotes] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'none' | one of NOTE_REF_TYPES
  const [recordFilter, setRecordFilter] = useState(''); // '' | 'type:id'
  const [editing, setEditing] = useState(null); // null | 'new' | note

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await listNotes(tenant);
      setNotes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.message || t('notes.loadFailed', 'Could not load notes'));
    } finally {
      setLoading(false);
    }
  }, [tenant, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    listNoteTargets(tenant)
      .then((res) => setTargets(flattenTargets(res.data)))
      .catch(() => setTargets([]));
  }, [tenant]);

  const targetsByType = useMemo(() => {
    const m = {};
    for (const tg of targets) (m[tg.type] = m[tg.type] || []).push(tg);
    return m;
  }, [targets]);

  const visibleNotes = useMemo(() => {
    let result = notes;

    if (typeFilter === 'none') {
      result = result.filter((n) => (n.links || []).length === 0);
    } else if (typeFilter !== 'all') {
      result = result.filter((n) => (n.links || []).some((l) => l.type === typeFilter));
    }

    if (recordFilter) {
      result = result.filter((n) => (n.links || []).some((l) => linkKey(l) === recordFilter));
    }

    const q = searchTerm.trim().toLowerCase();
    if (q) {
      result = result.filter((n) => {
        const hay = [
          n.title, n.body, n.createdByName, n.modifiedByName,
          ...(n.links || []).flatMap((l) => [l.label, l.sublabel]),
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    }

    if (dateFrom) {
      const from = new Date(`${dateFrom}T00:00:00`);
      result = result.filter((n) => { const d = toDate(n.modifiedon); return d ? d >= from : true; });
    }
    if (dateTo) {
      const to = new Date(`${dateTo}T23:59:59`);
      result = result.filter((n) => { const d = toDate(n.modifiedon); return d ? d <= to : true; });
    }

    return sortNotes(result);
  }, [notes, typeFilter, recordFilter, searchTerm, dateFrom, dateTo]);

  const clearFilters = () => {
    setSearchTerm(''); setDateFrom(''); setDateTo(''); setTypeFilter('all'); setRecordFilter('');
  };
  const filtersActive = searchTerm || dateFrom || dateTo || typeFilter !== 'all' || recordFilter;

  const goHome = () => navigate(`/${tenant}/dashboard`);
  const openNew = () => setEditing('new');

  // Picking a specific record narrows the type tab to match it.
  const onRecordChange = (value) => {
    setRecordFilter(value);
    if (value) setTypeFilter(value.split(':')[0]);
  };
  const onTypeChange = (key) => {
    setTypeFilter(key);
    if (recordFilter && !recordFilter.startsWith(`${key}:`)) setRecordFilter('');
  };

  const footerButtons = [
    { icon: <FaHome />, label: t('common.home', 'Home'), onClick: goHome },
    { icon: <FaPlus />, label: t('notes.newNote', 'New note'), onClick: openNew },
  ];

  if (loading) {
    return (
      <div className="feed-page">
        <div className="feed"><LoadingState messageKey="notes.loading" /></div>
      </div>
    );
  }

  const tabs = [
    { key: 'all', label: t('notes.filterAll', 'All') },
    { key: 'none', label: t('notes.filterUnassociated', 'Not associated') },
    ...NOTE_REF_TYPES.map((type) => ({ key: type, label: typePlural(t, type) })),
  ];

  return (
    <div className="feed-page notes-page">
      <div className="feed">
        <SearchFilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onClear={clearFilters}
          searchPlaceholder={t('notes.searchPlaceholder', 'Search notes…')}
        />

        <div className="notes-filters" style={{ gridColumn: '1 / -1' }}>
          <div className="notes-tabs" role="group" aria-label={t('notes.filterByType', 'Filter by association')}>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                aria-pressed={typeFilter === tab.key}
                onClick={() => onTypeChange(tab.key)}
                className={`notes-tab${typeFilter === tab.key ? ' notes-tab--active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <label className="surface notes-record-filter">
            <span>{t('notes.filterRecord', 'Associated with')}</span>
            <select value={recordFilter} onChange={(e) => onRecordChange(e.target.value)}>
              <option value="">{t('notes.anyRecord', 'Any open record')}</option>
              {NOTE_REF_TYPES.filter((type) => targetsByType[type]?.length).map((type) => (
                <optgroup key={type} label={typePlural(t, type)}>
                  {targetsByType[type].map((tg) => (
                    <option key={linkKey(tg)} value={linkKey(tg)}>
                      {tg.sublabel ? `${tg.label} — ${tg.sublabel}` : tg.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
        </div>

        <div className="desktop-action-bar" style={{ gridColumn: '1 / -1' }}>
          <button type="button" onClick={goHome}>
            <FaHome /> {t('common.home', 'Home')}
          </button>
          <IconButton label={t('notes.newNote', 'New note')} onClick={openNew}>
            <FaPlus aria-hidden="true" />
          </IconButton>
        </div>

        {error && <div style={{ gridColumn: '1 / -1' }}><ErrorState message={error} onRetry={load} /></div>}

        {!error && visibleNotes.length === 0 && (
          <div style={{ gridColumn: '1 / -1' }}>
            <EmptyState
              message={filtersActive
                ? t('notes.noMatches', 'No notes match your filters.')
                : t('notes.empty', 'No notes yet. Start one to keep track of ideas, decisions and to-dos.')}
            />
          </div>
        )}

        {!error && visibleNotes.length > 0 && (
          <ul className="notes-list" style={{ gridColumn: '1 / -1' }}>
            {visibleNotes.map((n) => (
              <li key={n.id} className="surface notes-list__item">
                <NoteCard note={n} tenantId={tenant} onOpen={() => setEditing(n)} />
              </li>
            ))}
          </ul>
        )}

        <AppFooter buttons={footerButtons} />
      </div>

      {editing && (
        <NoteEditorModal
          tenantId={tenant}
          note={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDeleted={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
